import { CATEGORY_MAP, BUILDING_COLORS } from './mapConstants';
import { image } from '../../api';

/**
 * Build HTML popup for a building GeoJSON feature (used in onEachFeature).
 * Fixes the bug where all categories showed "Residential".
 */
export function buildBuildingPopup(properties, memberCount) {
  const categoryId = properties.dictionary_building_category_id;
  const categoryName = CATEGORY_MAP[categoryId] || 'Unknown';
  const categoryColor = BUILDING_COLORS[categoryId] || '#666';
  const isResidential = categoryId === 25;
  const nameLabel = isResidential ? 'Household' : 'Building';
  const name = properties.name || '—';
  const brn = properties.brn || '—';
  const houseNo = properties.house_no || '—';
  const postcode = properties.postcode || '';
  const floors = (properties.number_of_floors != null && properties.number_of_floors !== '')
    ? properties.number_of_floors : '—';
  const verified = !!properties.is_verified;

  // Density label — "—" means unknown, 0 means empty/no data
  const hasCount = typeof memberCount === 'number';
  let densityBg = '#e2e8f0';
  let densityFg = '#475569';
  let densityLabel = '—';
  if (hasCount) {
    densityLabel = String(memberCount);
    if (memberCount === 0) { densityBg = '#f1f5f9'; densityFg = '#64748b'; }
    else if (memberCount <= 3) { densityBg = '#d1fae5'; densityFg = '#065f46'; }
    else if (memberCount <= 7) { densityBg = '#fef3c7'; densityFg = '#92400e'; }
    else if (memberCount <= 12) { densityBg = '#fed7aa'; densityFg = '#9a3412'; }
    else { densityBg = '#fecaca'; densityFg = '#991b1b'; }
  }

  const row = (label, value) =>
    '<div style="display:flex; justify-content:space-between; gap:12px; padding:4px 0; border-bottom:1px solid #f1f5f9;">' +
      '<span style="color:#64748b; font-size:12px;">' + label + '</span>' +
      '<span style="color:#0f172a; font-weight:600; font-size:12.5px; text-align:right;">' + value + '</span>' +
    '</div>';

  return (
    '<div style="width:280px; font-family: \'Segoe UI\', Roboto, Arial, sans-serif;">' +
      // Header: category chip + verified badge
      '<div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:8px;">' +
        '<span style="display:inline-flex; align-items:center; gap:6px; background:' + categoryColor + '22; color:' + categoryColor + '; font-weight:600; font-size:11px; padding:3px 9px; border-radius:999px; text-transform:uppercase; letter-spacing:0.5px;">' +
          '<span style="width:8px; height:8px; background:' + categoryColor + '; border-radius:50%;"></span>' + categoryName +
        '</span>' +
        (verified
          ? '<span style="background:#dcfce7; color:#166534; font-size:10px; font-weight:600; padding:2px 7px; border-radius:4px;">✓ VERIFIED</span>'
          : '<span style="background:#fef3c7; color:#92400e; font-size:10px; font-weight:600; padding:2px 7px; border-radius:4px;">UNVERIFIED</span>') +
      '</div>' +

      // Name
      '<div style="font-size:15px; font-weight:700; color:#0f172a; line-height:1.2; margin-bottom:2px;">' + name + '</div>' +
      '<div style="font-size:11px; color:#64748b; margin-bottom:10px;">' + nameLabel + ' · ' + brn + '</div>' +

      // Occupants highlight card
      '<div style="background:' + densityBg + '; border-radius:8px; padding:10px 12px; margin-bottom:10px; display:flex; align-items:center; justify-content:space-between;">' +
        '<div>' +
          '<div style="font-size:11px; color:' + densityFg + '; opacity:0.85; text-transform:uppercase; letter-spacing:0.5px; font-weight:600;">Occupants</div>' +
          '<div style="font-size:11px; color:' + densityFg + '; opacity:0.75;">' +
            (hasCount ? (memberCount === 0 ? 'No members registered' : memberCount === 1 ? '1 registered member' : memberCount + ' registered members') : 'Awaiting member data') +
          '</div>' +
        '</div>' +
        '<div style="font-size:28px; font-weight:700; color:' + densityFg + '; line-height:1;">' + densityLabel + '</div>' +
      '</div>' +

      // Detail rows
      row('House No.', houseNo) +
      (postcode ? row('Postcode', postcode) : '') +
      row('Floors', floors) +
    '</div>'
  );
}

/**
 * Build HTML popup for a boundary GeoJSON feature.
 */
export function buildBoundaryPopup(properties) {
  return (
    '<div style="width: 220px; font-size: 13px;">' +
    '<div class="fw-bolder mb-1" style="font-size:15px;">' + (properties.boundary_name || properties.name || 'N/A') + '</div>' +
    (properties.boundary_code ? '<div><span class="fw-bolder">Code:</span> ' + properties.boundary_code + '</div>' : '') +
    (properties.postcode ? '<div><span class="fw-bolder">Postcode:</span> ' + properties.postcode + '</div>' : '') +
    '</div>'
  );
}

/**
 * React component for the detailed building popup with image carousel.
 * Used when selecting a specific building from the sidebar list.
 */
export const BuildingDetailPopup = ({ dt, memberCount }) => {
  const getImageSrc = (index) => {
    if (dt.building_attachment && dt.building_attachment[index] && dt.building_attachment[index].file_path) {
      return image + dt.building_attachment[index].file_path;
    }
    return 'assets/demo/house-avatar.jpeg';
  };

  const carouselId = `carousel-${dt.id || 'default'}`;
  const isResidential = dt.dictionary_building_category && dt.dictionary_building_category.id === 25;

  return (
    <div className="h5" style={{ height: 620, width: 400 }}>
      <div className="row mb-3">
        <div className="col-auto">
          <div id={carouselId} className="carousel carousel-dark slide" data-bs-ride="carousel">
            <div className="carousel-indicators">
              <button type="button" data-bs-target={`#${carouselId}`} data-bs-slide-to="0" className="active" aria-current="true" aria-label="Slide 1"></button>
              <button type="button" data-bs-target={`#${carouselId}`} data-bs-slide-to="1" aria-label="Slide 2"></button>
              <button type="button" data-bs-target={`#${carouselId}`} data-bs-slide-to="2" aria-label="Slide 3"></button>
            </div>
            <div className="carousel-inner">
              {[0, 1, 2].map((i) => (
                <div key={i} className={`carousel-item ${i === 0 ? 'active' : ''}`} data-bs-interval={i === 0 ? 10000 : 2000}>
                  <img src={getImageSrc(i)} className="img img-responsive img-thumbnail d-block" style={{ height: 300, width: 350 }} alt={`Picture ${i + 1}`} />
                </div>
              ))}
            </div>
            <button className="carousel-control-prev" type="button" data-bs-target={`#${carouselId}`} data-bs-slide="prev">
              <span className="carousel-control-prev-icon" aria-hidden="true"></span>
              <span className="visually-hidden">Previous</span>
            </button>
            <button className="carousel-control-next" type="button" data-bs-target={`#${carouselId}`} data-bs-slide="next">
              <span className="carousel-control-next-icon" aria-hidden="true"></span>
              <span className="visually-hidden">Next</span>
            </button>
          </div>
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col-auto"><span className="fw-bolder">{isResidential ? 'Household Name' : 'Building Name'} &nbsp;:</span></div>
        <div className="col-auto mb-1">{dt.name || 'N/A'}</div>
        <hr />
        <div className="col-auto"><span className="fw-bolder">Postcode &nbsp;:</span></div>
        <div className="col-auto mb-1">{dt.boundary_id ? dt.boundary_id.boundary_code : 'N/A'}</div>
        <hr />
        <div className="col-auto"><span className="fw-bolder">Street &nbsp;:</span></div>
        <div className="col-auto mb-1">{dt.initials || 'N/A'}</div>
        <hr />
        <div className="col-auto"><span className="fw-bolder">House Number &nbsp;:</span></div>
        <div className="col-auto mb-1">{dt.postcode || 'N/A'}</div>
        <hr />
        <div className="col-auto"><span className="fw-bolder">Shehia Number &nbsp;:</span></div>
        <div className="col-auto mb-1">{dt.house_no || 'N/A'}</div>
        <hr />
        <div className="col-auto"><span className="fw-bolder">Building Uses &nbsp;:</span></div>
        <div className="col-auto mb-1">{dt.dictionary_building_category ? dt.dictionary_building_category.dictionary_item_name_en : 'N/A'}</div>
        <hr />
        <div className="col-auto"><span className="fw-bolder">Building BRN &nbsp;:</span></div>
        <div className="col-auto">{dt.brn || 'N/A'}</div>
        <hr />
        <div className="col-auto"><span className="fw-bolder">Registered Members &nbsp;:</span></div>
        <div className="col-auto">
          {typeof memberCount === 'number'
            ? <span className="fw-bolder" style={{ color: memberCount > 7 ? '#b91c1c' : memberCount > 3 ? '#92400e' : '#065f46' }}>{memberCount}</span>
            : <span className="text-muted">—</span>}
        </div>
      </div>
    </div>
  );
};
